import { getOccupancyLosScatterQueryOptions } from '@/entities/facility';
import { cn } from '@/shared/lib/cn';
import { useFacilityNavigation } from '@/shared/lib/hooks/use-facility-navigation';
import { OccupancyLosScatterChart } from '@/shared/ui/charts/occupancy-los-scatter-chart';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import type { FC } from 'react';

type OccupancyLosCardProps = {
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
  highlightFacilityName?: string;
  includeAllOrgFacilities?: boolean;
  heightClassName?: string;
  chartHeightClassName?: string;
};

export const OccupancyLosCard: FC<OccupancyLosCardProps> = ({
  dateRange,
  highlightFacilityName,
  includeAllOrgFacilities,
  heightClassName = 'h-[420px]',
  chartHeightClassName = 'h-[320px]',
}) => {
  const { navigateToFacility } = useFacilityNavigation();

  const { data, isLoading } = useQuery(
    getOccupancyLosScatterQueryOptions({
      from: dateRange.from.format('YYYY-MM-DD'),
      to: dateRange.to.format('YYYY-MM-DD'),
      includeAllOrgFacilities,
    })
  );

  const handlePointClick = (facilityName: string) => {
    if (highlightFacilityName) return;
    navigateToFacility({ facilityName });
  };

  if (isLoading) {
    return (
      <div className="bg-neutral-1 border-neutral-4 flex h-full items-center justify-center border">
        <div className="text-neutral-6">Loading...</div>
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className="bg-neutral-1 border-neutral-4 flex h-full items-center justify-center border">
        <div className="text-neutral-6">No data available</div>
      </div>
    );
  }

  const validData = data.data.filter(
    (point) => point.occupancy !== null && point.los !== null
  ) as Array<{ facilityName: string; occupancy: number; los: number }>;

  if (validData.length === 0) {
    return (
      <div className="bg-neutral-1 border-neutral-4 flex h-full items-center justify-center border">
        <div className="text-neutral-6">No valid data available</div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'bg-neutral-1 border-neutral-4 border px-6 py-2',
        heightClassName
      )}
    >
      <OccupancyLosScatterChart
        className="h-full w-full"
        data={validData}
        losThreshold={270}
        occupancyThreshold={80}
        chartClassName={chartHeightClassName}
        highlightFacilityName={highlightFacilityName}
        onPointClick={handlePointClick}
      />
    </div>
  );
};
